import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Card, Flex, Separator, Spinner } from '@radix-ui/themes'
import _ from 'lodash'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { deleteBudgetGoal, getBudgetGoal, updateBudgetGoal } from '~/features/budget/service'
import { UpdateBudgetGoalForm } from '~/features/budget/components/BudgetGoalForm'
import { SectionCard } from '~/components/SectionCard'
import LinkButton from '~/components/LinkButton'
import { ArrowLeftIcon } from '@radix-ui/react-icons'

import { Button, Callout } from '@radix-ui/themes'
import { BudgetChart } from '~/features/budget/components/BudgetChart'
import { getSite } from '~/features/sites/service'

const Component = () => {
  const { queryClient } = Route.useRouteContext()
  const { siteID } = Route.useParams()
  const navigate = Route.useNavigate()
  const { t } = useTranslation('sites')
  const { goalID } = Route.useParams()

  const { data: goal, isLoading } = useQuery({
    queryKey: ['budget-goal', goalID],
    queryFn: () => getBudgetGoal(goalID)
  })

  const { data: site, isLoading: isSiteLoading } = useQuery({
    queryKey: ['site', siteID],
    queryFn: () => getSite({ siteId: siteID })
  })

  const { mutate: updateGoal, isPending: isBudgetGoalPending } = useMutation({
    mutationFn: updateBudgetGoal,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['budget-goals', siteID]
      })
      queryClient.invalidateQueries({
        queryKey: ['budget-goal', goalID]
      })
      toast.success(t('budget.objectives.updateSuccess'))
    },
    onError: (error) => {
      console.error('Error creating budget goal', error)
      toast.error(t('budget.objectives.updateError'))
    }
  })

  const { mutate: deleteGoal, isPending: isDeleting } = useMutation({
    mutationFn: deleteBudgetGoal,
    onSuccess: () => {
      toast.success(t('budget.objectives.dangerZone.deleteSuccessMessage'))
      queryClient.invalidateQueries({
        queryKey: ['budget-goals', siteID]
      })
      navigate({
        to: '/sites/$siteID/config/budget',
        params: { siteID }
      })
    }
  })

  const handleDeleteClick = () => {
    window.confirm(t('budget.objectives.dangerZone.confirmDelete')) && deleteGoal(goalID)
  }

  if (isSiteLoading || isLoading || !goal || !site) {
    return (
      <Card className="min-h-48">
        <Spinner />
      </Card>
    )
  }

  return (
    <Flex gap={'6'} direction={'column'}>
      <SectionCard
        title={t('budget.objectives.updateTitle')}
        secondaryActions={
          <LinkButton
            to={'/sites/$siteID/config/budget'}
            params={{ siteID }}
            leadingIcon={<ArrowLeftIcon />}
            text={t('budget.objectives.backToButton')}
          />
        }
      >
        <Separator size={'4'} my={'2'} />
        <UpdateBudgetGoalForm siteID={siteID} onSubmit={updateGoal} isPending={isBudgetGoalPending} goal={goal} />
      </SectionCard>
      <BudgetChart site={site} goal={goal} showTitle />
      <SectionCard title={t('budget.objectives.dangerZone.title')}>
        <Callout.Root color="red" variant="surface" className="flex items-center justify-between">
          <Callout.Text>{t('budget.objectives.dangerZone.deleteWarning')}</Callout.Text>
          <Button variant="soft" onClick={handleDeleteClick} disabled={isDeleting}>
            {t('budget.objectives.dangerZone.deleteButton')}
          </Button>
        </Callout.Root>
      </SectionCard>
    </Flex>
  )
}

export const Route = createFileRoute('/sites/$siteID/config/budget/$goalID')({
  component: Component
})
